import { useEffect, useMemo, useRef, useState } from 'react';

import { MessageSquare } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useDataConnectors } from '@/hooks/useDataConnectors';
import { useStockSearch } from '@/hooks/useStockSearch';
import { stockAssistantApi } from '@/lib/api/stock-assistant.api';
import { isAbort, RATE_LIMIT_MESSAGE, serverMessage, statusOf } from '@/lib/stock-assistant-errors';

import { AskAiBar } from './AskAiBar';
import { ResultsChatDrawer } from './ResultsChatDrawer';
import { emptySuggestions, readSearchUrl, toSources, writeSearchUrl } from './search-state';
import { SORT_LABELS, StockFilters } from './StockFilters';
import { StockResultsGrid } from './StockResultsGrid';

import type { StockSearchState } from '@repo/shared-types';

const SEARCH_ERRORS: Record<string, string> = {
  postcode_not_recognised:
    "That postcode wasn't recognised — check it, or try just the first half (e.g. LE10).",
  radius_needs_postcode: 'A radius needs a postcode to measure from — add a postcode.',
};

export const StockSearchTab = () => {
  const [params, setParams] = useSearchParams();
  // Parse once per URL change so StockFilters gets a stable `state` between unrelated renders.
  const key = params.toString();
  const { state, page, source } = useMemo(() => readSearchUrl(new URLSearchParams(key)), [key]);
  const { data: connectors } = useDataConnectors();
  const connectorList = (connectors ?? []).map((c) => ({ id: c.id, name: c.name }));
  const sources = toSources(
    source,
    connectorList.map((c) => c.id),
  );
  const search = useStockSearch(state, page, sources);

  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [beforeAi, setBeforeAi] = useState<StockSearchState | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const askAbort = useRef<AbortController | null>(null);
  // A late AI reply must not move staff back to the search after they have left it.
  useEffect(() => () => askAbort.current?.abort(), []);

  // Any filter change starts again at page 1.
  const setState = (next: StockSearchState) =>
    setParams((prev) => writeSearchUrl(next, 1, source, prev));
  const setPage = (p: number) => {
    setParams((prev) => writeSearchUrl(state, p, source, prev));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const setSource = (s: string) => setParams((prev) => writeSearchUrl(state, 1, s, prev));

  const askAi = async (message: string) => {
    askAbort.current?.abort();
    const controller = new AbortController();
    askAbort.current = controller;
    setAiBusy(true);
    setAiError(null);
    try {
      const res = await stockAssistantApi.interpret({ message, state, sources }, controller.signal);
      if (controller.signal.aborted) return;
      setBeforeAi(state);
      setAiSummary(res.summary);
      setState(res.state);
    } catch (err) {
      if (controller.signal.aborted || isAbort(err)) return;
      setAiError(
        statusOf(err) === 429
          ? RATE_LIMIT_MESSAGE
          : (serverMessage(err) ?? 'The AI is unavailable — use the filters below.'),
      );
    } finally {
      if (askAbort.current === controller) setAiBusy(false);
    }
  };

  const undoAi = () => {
    if (!beforeAi) return;
    setState(beforeAi);
    setBeforeAi(null);
    setAiSummary(null);
  };

  const result = search.data;
  const total = result
    ? `${result.total.toLocaleString('en-GB')}${result.total_capped ? '+' : ''}`
    : null;
  const summary = aiSummary && total !== null ? `${aiSummary} · ${total} cars` : aiSummary;
  const connectorName = (id: string | null | undefined) =>
    id ? (connectorList.find((c) => c.id === id)?.name ?? 'Connector') : 'DealerKit';

  let countText = '';
  if (search.isError) {
    countText = 'Search failed — try again.';
  } else if (total !== null) {
    const sort = state.sort ? ` · ${SORT_LABELS[state.sort] ?? state.sort}` : '';
    const near = result?.origin
      ? ` · near ${result.origin.label}${result.radius_miles_used ? ` (${result.radius_miles_used} mi)` : ' (nationwide)'}`
      : '';
    countText = `${total} cars${sort}${near}`;
  }

  return (
    <div className="space-y-4">
      <AskAiBar
        onAsk={askAi}
        busy={aiBusy}
        summary={summary}
        canUndo={!!beforeAi}
        onUndo={undoAi}
        error={aiError}
      />
      <StockFilters
        state={state}
        onChange={setState}
        source={source}
        onSourceChange={setSource}
        connectors={connectorList}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p aria-live="polite" className="text-muted-foreground text-sm">
          {countText}
        </p>
        <Button variant="outline" size="sm" onClick={() => setChatOpen(true)}>
          <MessageSquare className="mr-1 h-4 w-4" aria-hidden />
          Ask about these results
        </Button>
      </div>

      {result?.error && SEARCH_ERRORS[result.error] && (
        <p role="alert" className="text-destructive text-sm">
          {SEARCH_ERRORS[result.error]}
        </p>
      )}

      <StockResultsGrid
        hits={result?.vehicles}
        page={page}
        totalPages={result?.totalPages ?? 1}
        loading={search.isFetching}
        isError={search.isError}
        onRetry={() => void search.refetch()}
        sourceLabel={(hit) => connectorName(hit.connector_id)}
        onPage={setPage}
        emptyActions={emptySuggestions(state).map((s) => ({
          label: s.label,
          onClick: () => setState({ ...state, ...s.patch } as StockSearchState),
        }))}
      />

      <ResultsChatDrawer
        open={chatOpen}
        onOpenChange={setChatOpen}
        state={state}
        sources={sources}
      />
    </div>
  );
};
